import { Layers } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { UsPhoneInput } from "@/components/ui/phone-input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { FirmOptions } from "@/features/firm-options";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { accidentFieldsInput, type SameAccident } from "@/lib/call-new-contact";
import { contactName, stageText } from "@/lib/contacts";
import { STATE_CHOICES, firmsToName } from "@/lib/firms";
import { useFormChanged, zodResolver } from "@/lib/form";
import {
  CONTACT_CHANNELS, contactChannelInput, contactChannelOf,
} from "@/lib/lead-card";
import {
  useCampaigns, useCreateContact, useFirms, useLeadHistory, useUpdateContact, type ContactCreateInput,
  type ContactInput,
} from "@/lib/queries";
import { siblingHistory } from "@/lib/same-phone";
import { SEEN_BEFORE, emailToAsk, phoneToAsk } from "@/lib/seen-before";
import { atLeast, type CallerRelation, type LeadCard, type LeadSibling } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { US_PHONE_PROBLEM, usPhone } from "@/lib/phone-number";
import { cn, formatPhoneNumber } from "@/lib/utils";

/**
 * Create or edit a contact (ADR-044).
 *
 * Who edits what mirrors core-api's `PATCH /leads/{id}`: a rep the names, the language, the residence
 * state (the rep on the call is who hears where the lead is) and the e-mail (Send contract needs it,
 * ACL-541); a supervisor also the phone and the firm. The phone and the state each leave a system note:
 * they drive quiet hours; the e-mail does too, naming no address. Nobody the source, the stage or the
 * dialer's columns, so none of those is a field. The form shows a rep the supervisor's fields disabled
 * rather than hiding them: the record says the phone is there and a rep should see why they cannot
 * change it.
 *
 * A new contact's firm is the rep's to pick too (ACL-644, platform ADR-208), from the active firms
 * only: core-api refuses a rep one that takes no new leads. The rep reaches a contact they made, so
 * "Contact created" opens its card for them as it does for a supervisor. On Edit the firm stays a
 * supervisor's field.
 *
 * The communication preference (ACL-343) is anyone's to change who may edit the contact, a rep
 * included: Calls and texts, Text first (no automatic calls for 24 hours from the save, then back to
 * calls and texts on its own), or SMS only (never called by the dialer). It shows only once core-api
 * serves it, so an edit never sends a field the API would refuse.
 *
 * The campaign list on a new contact is an admin's (ACL-645, platform ADR-211): core-api refuses it
 * from anyone below, so the field does not show. A supervisor adds a list afterwards, from the record.
 *
 * A phone that already has a record is a 409 naming it; the form offers the record, and "Create
 * anyway" for the case the design allows — a second record for another firm, as GoHighLevel kept
 * them. Before that, while the rep types a new contact's phone or e-mail, the form lists the records
 * that already have it, "Seen before" (`TypedHistory`, ACL-409), and saving is unchanged.
 *
 * Opened from an inbound call whose caller we have no record of (`fromCall`, ACL-285), the phone is
 * the caller's number, the rep says who is calling (`caller_relation`), and there is no campaign
 * list to put them on: an unknown caller is not a lead because they rang, so nothing here dials
 * them. A record that already has the number can be named for the call instead (`onUseExisting`).
 * The phone is locked to the caller's (the call is linked only to a contact with the calling number).
 *
 * Nobody is asked for consent (ACL-575, platform ADR-194): the form has no consent fields, and
 * core-api writes the fixed evidence on every contact made here.
 *
 * Where the lead is, is one State list and no timezone (ACL-576, platform ADR-197): the lead is on
 * their state's clock, and a new state clears a zone an import stored, so their calls, texts and
 * callbacks follow the state picked here. A stored state outside the list stays pickable, so opening
 * and saving does not quietly change it.
 *
 * Opened from the call's card for another person in the lead's accident (`sameAccident`, ACL-626), the
 * form starts with the lead's Date of accident and City of accident, side by side at the top, each the
 * rep's to change. Saving sends the lead and the two answers with the contact, and core-api creates it,
 * stores them and links it to the lead's accident at once (platform ADR-203).
 */

const NONE = "none";

export const CALLER_RELATIONS: { value: CallerRelation; label: string }[] = [
  { value: "potential_lead", label: "Potential new lead" },
  { value: "family", label: "Family member of a client" },
  { value: "friend_referral", label: "Friend or referral" },
  { value: "other", label: "Someone else" },
];

const schema = z.object({
  phone: z.string().min(3, "A number is required").max(40),
  first_name: z.string().max(120),
  last_name: z.string().max(120),
  email: z.string().max(254).refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), "That does not look like an e-mail"),
  language: z.string(),
  contact_channel: z.enum(["calls_and_sms", "text_first", "sms_only"]),
  state: z.string(),
  firm_id: z.string(),
  campaign_id: z.string(),
  caller_relation: z.string(),
  accident_date: z.string(),
  accident_city: z.string().max(200),
});

type FormValues = z.infer<typeof schema>;

function valuesOf(contact: LeadCard | undefined, phone?: string, accident?: SameAccident): FormValues {
  return {
    phone: contact ? formatPhoneNumber(contact.phone) : (phone ?? ""),
    first_name: contact?.first_name ?? "",
    last_name: contact?.last_name ?? "",
    email: contact?.email ?? "",
    language: contact?.language ?? NONE,
    contact_channel: contactChannelOf(contact ?? {}),
    state: contact?.state ?? NONE,
    firm_id: contact?.firm ? String(contact.firm.id) : NONE,
    campaign_id: NONE,
    caller_relation: NONE,
    accident_date: accident?.date?.value ?? "",
    accident_city: accident?.city?.value ?? "",
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);
const pick = (v: string) => (v === NONE ? null : v);

export function ContactFormDialog({
  open, onOpenChange, contact, onCreated, fromCall, onUseExisting, sameAccident,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  contact?: LeadCard;
  onCreated?: (card: LeadCard) => void;
  /** Create for the unknown caller on the rep's call (ACL-285): their number, as the ACD has it. */
  fromCall?: { phone: string };
  /** A record with the phone exists: offer to name it for the call instead of a second one. */
  onUseExisting?: (leadId: string) => void;
  /** Create another person in this lead's accident, from the call's card (ACL-626). */
  sameAccident?: SameAccident;
}) {
  const editing = Boolean(contact);
  const { user } = useAuth();
  const supervisor = atLeast(user?.role, "supervisor");
  const admin = atLeast(user?.role, "admin");
  const toast = useToast();
  const create = useCreateContact();
  const update = useUpdateContact();
  const firms = useFirms({ enabled: open });
  const campaigns = useCampaigns({}, { enabled: open && !editing && admin && !fromCall });
  const [formError, setFormError] = React.useState<string | null>(null);
  const [existing, setExisting] = React.useState<string | null>(null);

  const callerPhone = fromCall?.phone;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: valuesOf(contact, callerPhone, sameAccident),
  });
  const { register, handleSubmit, setValue, setError, watch, reset, formState: { errors } } = form;

  // The lead's accident answers are taken as the form opens, and only then: a later read of the lead
  // (its form is polled) must not empty what the rep has typed since.
  const accidentLead = sameAccident?.leadId;
  React.useEffect(() => {
    if (!open) return;
    reset(valuesOf(contact, callerPhone, sameAccident));
    setFormError(null);
    setExisting(null);
  }, [open, contact, callerPhone, reset, accidentLead]);

  const changed = useFormChanged(form);
  const busy = create.isPending || update.isPending;

  const submit = (values: FormValues, duplicate = false) => {
    setFormError(null);
    setExisting(null);
    // A US number, stored with its +1 (ACL-653). The caller's own number and one already on the
    // record, whatever its spelling, are not the rep's to fix here.
    const typed = values.phone.trim();
    const us = usPhone(typed);
    const kept = fromCall !== undefined || (contact !== undefined && typed === formatPhoneNumber(contact.phone));
    if (!us && !kept && (!editing || supervisor)) {
      setError("phone", { message: US_PHONE_PROBLEM });
      return;
    }
    const phone = kept ? (contact?.phone ?? typed) : (us ?? typed);
    if (editing && contact) {
      const input: ContactInput & { id: string } = { id: contact.id };
      if (values.first_name.trim() !== (contact.first_name ?? "")) input.first_name = orNull(values.first_name);
      if (values.last_name.trim() !== (contact.last_name ?? "")) input.last_name = orNull(values.last_name);
      if (pick(values.language) !== contact.language) input.language = pick(values.language);
      if (pick(values.state) !== contact.state) input.state = pick(values.state);
      const channel = contactChannelInput(contact, values.contact_channel);
      if (channel !== undefined) input.contact_channel = channel;
      if (values.email.trim() !== (contact.email ?? "")) input.email = orNull(values.email);
      if (supervisor) {
        if (phone !== contact.phone) input.phone = phone;
        const firm = values.firm_id === NONE ? null : Number(values.firm_id);
        if (firm !== null && firm !== (contact.firm?.id ?? null)) input.firm_id = firm;
      }
      if (Object.keys(input).length === 1) {
        onOpenChange(false);
        return;
      }
      update.mutate(input, {
        onSuccess: () => {
          toast.success("Contact saved");
          onOpenChange(false);
        },
        onError: (e) => setFormError(e instanceof ApiError ? e.message : "Could not save the contact."),
      });
      return;
    }
    if (fromCall && values.caller_relation === NONE) {
      setError("caller_relation", { message: "Say who is calling" });
      return;
    }
    const input: ContactCreateInput = {
      phone,
      first_name: orNull(values.first_name),
      last_name: orNull(values.last_name),
      email: orNull(values.email),
      language: pick(values.language),
      state: pick(values.state),
      firm_id: values.firm_id === NONE ? null : Number(values.firm_id),
      campaign_id: fromCall ? null : pick(values.campaign_id),
      caller_relation: fromCall ? (values.caller_relation as CallerRelation) : undefined,
      duplicate,
      ...(sameAccident
        ? {
            same_accident_as: sameAccident.leadId,
            fields: accidentFieldsInput(sameAccident, { date: values.accident_date, city: values.accident_city }),
          }
        : {}),
    };
    create.mutate(input, {
      onSuccess: (card) => {
        toast.success("Contact created", contactName(card) ?? card.phone);
        onOpenChange(false);
        onCreated?.(card);
      },
      onError: (e) => {
        const body = e instanceof ApiError ? (e.body as { code?: string; existing_lead_id?: string } | undefined) : undefined;
        if (body?.code === "phone_exists" && body.existing_lead_id) {
          setExisting(body.existing_lead_id);
          return;
        }
        setFormError(e instanceof ApiError ? e.message : "Could not create the contact.");
      },
    });
  };

  const lockedHint = supervisor ? undefined : "A supervisor changes this";
  const channelServed = editing && contact?.contact_channel !== undefined;
  const channelHint = CONTACT_CHANNELS.find((c) => c.value === watch("contact_channel"))?.hint;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="md">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? "Edit contact"
              : fromCall
                ? "New contact for this caller"
                : sameAccident
                  ? "New contact in this accident"
                  : "New contact"}
          </DialogTitle>
          <DialogDescription>
            {editing
              ? "The facts a person may change. The stage and the dialer's own state are not among them."
              : fromCall
                ? "The caller's number is filled in. The contact goes on no dial list, so nothing dials them because they called."
                : sameAccident
                  ? `Another person in the same accident as ${sameAccident.name ?? "this lead"}. The accident's date and city are filled in from their record; change them if they differ. Saving links the two records.`
                  : "A contact typed in here starts as New."}
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={handleSubmit((values) => submit(values))}>
          <DialogBody className="space-y-4">
            <DiscardGuard changed={changed} />
            {sameAccident && (sameAccident.date || sameAccident.city) && (
              <div className="grid grid-cols-2 gap-3">
                {sameAccident.date && (
                  <Field label="Date of accident" htmlFor="contact-accident-date">
                    <Input {...register("accident_date")} type="date" />
                  </Field>
                )}
                {sameAccident.city && (
                  <Field label="City of accident" htmlFor="contact-accident-city" error={errors.accident_city?.message}>
                    <Input {...register("accident_city")} autoComplete="off" />
                  </Field>
                )}
              </div>
            )}
            {fromCall && (
              <Field label="Who is calling" htmlFor="contact-relation" required error={errors.caller_relation?.message}>
                <div id="contact-relation" role="radiogroup" aria-label="Who is calling" className="grid grid-cols-2 gap-2">
                  {CALLER_RELATIONS.map((r) => {
                    const on = watch("caller_relation") === r.value;
                    return (
                      <button
                        key={r.value}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setValue("caller_relation", r.value, { shouldDirty: true, shouldValidate: true })}
                        className={cn(
                          "rounded-md border px-2.5 py-1.5 text-left text-sm",
                          on
                            ? "border-accent-8 bg-accent-3 font-medium text-accent-11"
                            : "border-border bg-surface text-text hover:bg-surface-hover",
                        )}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" htmlFor="contact-first" error={errors.first_name?.message}>
                <Input {...register("first_name")} autoComplete="off" />
              </Field>
              <Field label="Last name" htmlFor="contact-last" error={errors.last_name?.message}>
                <Input {...register("last_name")} autoComplete="off" />
              </Field>
            </div>
            <Field
              label="Phone"
              htmlFor="contact-phone"
              required
              error={errors.phone?.message}
              hint={
                editing
                  ? lockedHint
                  : fromCall
                    ? "The number calling. The call is linked to a contact with this number only."
                    : undefined
              }
            >
              {/* The +1 is fixed; the rep types the ten digits after it (ACL-653). */}
              <UsPhoneInput
                id="contact-phone"
                name="phone"
                value={watch("phone")}
                invalid={Boolean(errors.phone)}
                disabled={editing && !supervisor}
                readOnly={Boolean(fromCall)}
                onChange={(digits) => setValue("phone", digits, { shouldDirty: true, shouldValidate: Boolean(errors.phone) })}
              />
            </Field>
            <Field label="E-mail" htmlFor="contact-email" error={errors.email?.message}>
              <Input {...register("email")} type="email" />
            </Field>
            {!editing && (
              <TypedHistory phone={watch("phone")} email={watch("email")} onOpen={() => onOpenChange(false)} />
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Language" htmlFor="contact-language">
                <Select value={watch("language")} onValueChange={(v) => setValue("language", v, { shouldDirty: true })}>
                  <SelectTrigger id="contact-language" aria-label="Language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not set</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label="Residence state"
                htmlFor="contact-state"
                hint="Where the lead lives: their calls, texts and callbacks follow its clock"
              >
                <Select value={watch("state")} onValueChange={(v) => setValue("state", v, { shouldDirty: true })}>
                  <SelectTrigger id="contact-state" aria-label="Residence state">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not set</SelectItem>
                    {STATE_CHOICES.map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label}
                      </SelectItem>
                    ))}
                    {contact?.state && !STATE_CHOICES.some((s) => s.key === contact.state) && (
                      <SelectItem value={contact.state}>{contact.state}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            {channelServed && (
              <Field
                label="Communication preference"
                htmlFor="contact-channel"
                hint={channelHint}
              >
                <Select
                  value={watch("contact_channel")}
                  onValueChange={(v) => setValue("contact_channel", v as FormValues["contact_channel"], { shouldDirty: true })}
                >
                  <SelectTrigger id="contact-channel" aria-label="Communication preference">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_CHANNELS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {(supervisor || !editing) && (
              <Field
                label="Firm"
                htmlFor="contact-firm"
                hint={
                  editing
                    ? "A contact keeps a firm once it has one; it can be changed, not cleared."
                    : supervisor
                      ? undefined
                      : "The firm taking the case. Firms not taking new leads are not listed."
                }
              >
                <Select value={watch("firm_id")} onValueChange={(v) => setValue("firm_id", v, { shouldDirty: true })}>
                  <SelectTrigger id="contact-firm" aria-label="Firm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(!editing || !contact?.firm) && <SelectItem value={NONE}>No firm yet</SelectItem>}
                    <FirmOptions firms={firmsToName(firms.data ?? [], supervisor)} state={watch("state")} />
                  </SelectContent>
                </Select>
              </Field>
            )}
            {!editing && admin && !fromCall && (
              <Field label="Put on a campaign's list" htmlFor="contact-campaign" hint="The calling campaign, usually. A do-not-call or signed record with the phone, or a paused firm, keeps it off.">
                <Select value={watch("campaign_id")} onValueChange={(v) => setValue("campaign_id", v, { shouldDirty: true })}>
                  <SelectTrigger id="contact-campaign" aria-label="Campaign">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No list</SelectItem>
                    {(campaigns.data ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {existing && (
              <Callout tone="warning">
                A contact with this phone exists:{" "}
                <Link to={`/contacts/${existing}`} className="underline" onClick={() => onOpenChange(false)}>
                  open it
                </Link>
                . Create a second record only for another firm.
                <div className="mt-2 flex flex-wrap gap-2">
                  {onUseExisting && (
                    <Button type="button" size="sm" onClick={() => onUseExisting(existing)}>
                      Link this call to it
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="outline" loading={busy} onClick={handleSubmit((v) => submit(v, true))}>
                    Create anyway
                  </Button>
                </div>
              </Callout>
            )}
            {formError && <Callout tone="danger">{formError}</Callout>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={busy}>
              {editing ? "Save" : fromCall ? "Create and link to call" : "Create contact"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The records a new contact's phone or e-mail already has, while the rep types it (ACL-409,
 * `GET /leads/history`): each by its name where the rep may open it, with Open, which closes the form
 * as the 409's "open it" does; a record the rep may not open by its firm and stage alone, as the Same
 * phone card shows one. It decides nothing: the rep can still create, and the 409 is as it was. Asked
 * once the rep stops typing, and only for a whole number or an address.
 */
function TypedHistory({ phone, email, onOpen }: { phone: string; email: string; onOpen: () => void }) {
  const askPhone = useDebounced(phoneToAsk(phone), 400);
  const askEmail = useDebounced(emailToAsk(email), 400);
  const history = useLeadHistory(askPhone, askEmail);
  // The query keeps its last answer while the next is on its way; with nothing worth asking about
  // there is no next one, and the last must not stay.
  if (askPhone === null && askEmail === null) return null;
  const byPhone = history.data?.same_phone ?? [];
  const byEmail = history.data?.same_email ?? [];
  if (byPhone.length + byEmail.length === 0) return null;
  // The phone's records are listed twenty at most, and counted whole (an older core-api: as listed).
  const phoneTotal = Math.max(history.data?.same_phone_total ?? 0, byPhone.length);
  const counts = [
    phoneTotal > 0 && `${phoneTotal} with this phone`,
    byEmail.length > 0 && `${byEmail.length} with this e-mail`,
  ].filter(Boolean);
  return (
    <section aria-label={SEEN_BEFORE} className="rounded-md border border-border px-3 py-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-text">
        <Layers className="size-3.5 shrink-0 text-text-muted" aria-hidden />
        {SEEN_BEFORE} · {counts.join(" · ")}
      </p>
      <ul className="mt-1 divide-y divide-border">
        {byPhone.map((record) => (
          <TypedRecord key={record.id} record={record} why="Same phone" onOpen={onOpen} />
        ))}
        {byEmail.map((record) => (
          <TypedRecord key={record.id} record={record} why="Same e-mail" onOpen={onOpen} />
        ))}
      </ul>
      {phoneTotal > byPhone.length && history.data?.phone && (
        // As the Same phone card's own "View all", by the contacts list searched by the number.
        <Link
          to={`/contacts?q=${encodeURIComponent(history.data.phone)}`}
          onClick={onOpen}
          className="mt-1 inline-flex text-xs font-medium text-accent-11 hover:underline"
        >
          View all {phoneTotal} with this phone
        </Link>
      )}
      <p className="mt-1 text-2xs text-text-muted">
        They share a phone or an e-mail, and nothing more is known. You can still create this contact.
      </p>
    </section>
  );
}

function TypedRecord({ record, why, onOpen }: { record: LeadSibling; why: string; onOpen: () => void }) {
  const history = siblingHistory(record);
  const stage = stageText(record.stage);
  const title = history.deleted ? "Deleted record" : history.mayOpen ? (history.name ?? "No name") : "Restricted record";
  return (
    <li className="flex items-center gap-2 py-1.5 text-xs">
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate font-medium", history.mayOpen ? "text-text" : "text-text-muted")}>
          {title}
        </span>
        <span className="block truncate text-text-muted">
          {record.firm?.label ?? "No firm"} · {why}
        </span>
      </span>
      {record.dnc && record.stage !== "dnc" && (
        <Badge tone="danger" size="sm">
          DNC
        </Badge>
      )}
      <Badge tone={stage.tone} size="sm" title={stage.description}>
        {stage.label}
      </Badge>
      {history.mayOpen && (
        <Link
          to={`/contacts/${record.id}`}
          onClick={onOpen}
          className="shrink-0 font-medium text-accent-11 hover:underline"
        >
          Open
        </Link>
      )}
    </li>
  );
}
